/**
 * Fantasy Console 0 - Editor History Hook
 *
 * Generic undo/redo history for editors that work with Uint8Array data
 * (sprite sheets, map data, etc.). Uses refs for history storage to
 * avoid stale-closure issues in canvas event handlers.
 */

import { useRef, useCallback, useState } from 'react'

const DEFAULT_HISTORY_LIMIT = 64

export function useEditorHistory(
  onDataChange: (data: Uint8Array) => void,
  limit: number = DEFAULT_HISTORY_LIMIT,
) {
  const historyRef = useRef<Uint8Array[]>([])
  const positionRef = useRef(-1)
  const onDataChangeRef = useRef(onDataChange)
  onDataChangeRef.current = onDataChange

  // Revision counter triggers re-renders so canUndo/canRedo stay accurate
  const [, setRevision] = useState(0)

  /** Record a new state and propagate the data change */
  const pushState = useCallback(
    (data: Uint8Array) => {
      // Discard any redo history
      historyRef.current = historyRef.current.slice(0, positionRef.current + 1)
      // Store a clone so history entries stay immutable
      historyRef.current.push(new Uint8Array(data))
      // Trim oldest entries if over limit
      while (historyRef.current.length > limit) {
        historyRef.current.shift()
      }
      positionRef.current = historyRef.current.length - 1
      setRevision((n) => n + 1)
      onDataChangeRef.current(data)
    },
    [limit],
  )

  /** Undo to the previous state */
  const undo = useCallback(() => {
    if (positionRef.current <= 0) return
    positionRef.current -= 1
    setRevision((n) => n + 1)
    onDataChangeRef.current(
      new Uint8Array(historyRef.current[positionRef.current]),
    )
  }, [])

  /** Redo to the next state */
  const redo = useCallback(() => {
    if (positionRef.current >= historyRef.current.length - 1) return
    positionRef.current += 1
    setRevision((n) => n + 1)
    onDataChangeRef.current(
      new Uint8Array(historyRef.current[positionRef.current]),
    )
  }, [])

  /** Reset history to a single entry (e.g. when loading a new cart) */
  const resetHistory = useCallback((data: Uint8Array) => {
    historyRef.current = [new Uint8Array(data)]
    positionRef.current = 0
    setRevision((n) => n + 1)
  }, [])

  return {
    pushState,
    undo,
    redo,
    canUndo: positionRef.current > 0,
    canRedo: positionRef.current < historyRef.current.length - 1,
    resetHistory,
  }
}
